import React from "react";
import { Page, Text, View, Document, StyleSheet, Image, PDFViewer } from "@react-pdf/renderer";
import { dateToFullString, dateToString } from "utils/dateUtils";
import { taskStatuses } from "utils/static/workflowsStaticData";
import { hasElements } from "utils/arrays";
import { Tenant } from "utils/tenants";

const statusStyles = taskStatuses.reduce((map, item) => {
    return {
        ...map,
        [`statusBar_${item.value}`]: {
            flexDirection: "row",
            alignContent: "flex-start",
            width: "100%",
            padding: 5,
            maxHeight: 40,
            fontSize: 10,
            ...item.style,
        },
    };
}, {});

const styles = StyleSheet.create({
    page: {
        paddingTop: 20,
        paddingBottom: 65,
        paddingHorizontal: 10,
    },
    divider: {
        width: "100%",
        height: 1,
        backgroundColor: "black",
        marginVertical: 20,
    },
    header: {
        flexDirection: "row",
        marginVertical: 10,
        justifyContent: "space-between",
        alignItems: "center",
    },
    logo: {
        height: 64,
        width: 87,
    },
    title: {
        fontSize: 18,
        fontWeight: "bold",
        textAlign: "center",
        width: "100%",
    },
    pageNumber: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 10,
        right: 10,
        textAlign: "right",
        color: "grey",
    },
    time: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 10,
        right: 10,
        textAlign: "left",
        color: "grey",
    },
    printedBy: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 10,
        right: 10,
        textAlign: "center",
        color: "grey",
    },
    footer: {
        position: "absolute",
        bottom: 40,
        left: 10,
        right: 10,
    },
    detail: {
        width: "100%",
    },
    tableHead: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 10,
        backgroundColor: "silver",
        width: "100%",
        height: 20,
    },
    columnHeader: {
        fontSize: 10,
        fontWeight: "700",
        color: "black",
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 10,
    },
    tableBody: {
        borderWidth: 1,
        borderColor: "silver",
    },
    row: {
        flexDirection: "row",
        alignContent: "flex-start",
        padding: 2,
        borderBottomWidth: 1,
        borderBottomColor: "silver",
    },
    oddRow: {
        backgroundColor: "whitesmoke",
    },
    cell: {
        fontSize: 8,
        paddingHorizontal: 2,
        paddingVertical: 4,
        flex: 1,
        textAlign: "left",
        marginLeft: 10,
    },
    ...statusStyles,
    alertIcon_aboutToExpire: {
        color: "#ffcc00",
        width: 16,
        height: 16,
    },
    alertIcon_expired: {
        color: "#ff3333",
        width: 16,
        height: 16,
    },
});

const MyDocument = ({ data, currentUser }) => {
    const groupedData = taskStatuses.reduce(
        (map, status) => ({ ...map, [status.value]: data.filter((x) => x.status === status.value) }),
        {}
    );

    return (
        <PDFViewer width="100%" height="900px">
            <Document>
                <Page size="A4" style={styles.page}>
                    <View style={styles.header} fixed>
                        <Text style={styles.title}>TEAM WORK REPORT</Text>
                        <Image style={styles.logo} src={Tenant.getLogo()} />
                    </View>

                    <View style={styles.divider} fixed />

                    <View style={styles.detail}>
                        <View style={styles.tableHead} fixed>
                            <View style={{ ...styles.columnHeader, flexBasis: "15%" }}>
                                <Text>Assignee</Text>
                            </View>
                            <View style={{ ...styles.columnHeader, flexBasis: "25%" }}>
                                <Text>Task</Text>
                            </View>
                            <View style={{ ...styles.columnHeader, flexBasis: "15%" }}>
                                <Text>Due Date</Text>
                            </View>
                            <View style={{ ...styles.columnHeader, flexBasis: "15%" }}>
                                <Text>Start Date</Text>
                            </View>
                            <View style={{ ...styles.columnHeader, flexBasis: "15%" }}>
                                <Text>End Date</Text>
                            </View>
                            <View style={{ ...styles.columnHeader, flexBasis: "15%" }}>
                                <Text>Case</Text>
                            </View>
                        </View>
                        {taskStatuses.map((status) => {
                            const statusData = groupedData[status.value];

                            return (
                                hasElements(statusData) && (
                                    <View style={styles.tableBody}>
                                        <View key={`status-bar-${status.value}`} style={styles[`statusBar_${status.value}`]}>
                                            <Text>{status.label.toUpperCase()}</Text>
                                        </View>
                                        {statusData.map((task, index) => {
                                            return (
                                                <View key={index} style={{ ...styles.row, ...(index % 2 === 1 ? styles.oddRow : {}) }}>
                                                    <View style={{ ...styles.cell, flexBasis: "15%" }}>
                                                        <Text>{task.assigneeUserFullName || "--"}</Text>
                                                    </View>
                                                    <View style={{ ...styles.cell, flexBasis: "25%" }}>
                                                        <Text>{task.name}</Text>
                                                    </View>
                                                    <View style={{ ...styles.cell, flexBasis: "15%" }}>
                                                        <Text>{task.dueDate ? dateToString(task.dueDate) : "--"}</Text>
                                                    </View>
                                                    <View style={{ ...styles.cell, flexBasis: "15%" }}>
                                                        <Text>{task.startDate ? dateToString(task.startDate) : "--"}</Text>
                                                    </View>
                                                    <View style={{ ...styles.cell, flexBasis: "15%" }}>
                                                        <Text>{task.endDate ? dateToString(task.endDate) : "--"}</Text>
                                                    </View>
                                                    <View style={{ ...styles.cell, flexBasis: "15%" }}>
                                                        <Text>{task.moduleInfo?.fileNo || "--"}</Text>
                                                    </View>
                                                </View>
                                            );
                                        })}
                                    </View>
                                )
                            );
                        })}
                    </View>

                    <View style={styles.footer}>
                        <View style={styles.divider} />
                    </View>

                    <Text style={styles.time} render={() => dateToFullString(new Date())} fixed />

                    <Text style={styles.printedBy} render={() => `Printed by ${currentUser.name}`} fixed />

                    <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
                </Page>
            </Document>
        </PDFViewer>
    );
};

export default MyDocument;
